import { useCallback, useState } from 'react'
import { useTranslation } from '@/translations/i18n'
import {
	CATEGORY_COLUMN_WIDTH,
	CATEGORY_PADDING_Y,
	LANE_GAP,
	LANE_HEIGHT,
	Z_CATEGORY_COLUMN
} from '../constants'
import type { ActivityLane, RoadmapActivity } from '../types'
import { GanttActivityBar } from './gantt-activity-bar'

interface GanttCategoryRowProps {
	title: string
	lanes: ActivityLane[]
	dayWidth: number
	timelineWidth: number
	onActivitySelect?: (activity: RoadmapActivity) => void
}

function GanttCategoryRow({
	title,
	lanes,
	dayWidth,
	timelineWidth,
	onActivitySelect
}: GanttCategoryRowProps) {
	const { t } = useTranslation()
	/**
	 * Bars report their height here because a wrapped label makes them taller
	 * than one lane, and only they can know by how much. Lanes are then stacked
	 * cumulatively rather than by index: multiplying by a fixed lane height
	 * would let a two-line bar overlap the lane beneath it.
	 */
	const [barHeights, setBarHeights] = useState<Record<string, number>>({})

	const handleHeightChange = useCallback((id: string, height: number) => {
		// Same object back when nothing moved: a bar reports on every measure,
		// and a fresh object each time would re-render the row forever.
		setBarHeights((current) =>
			current[id] === height ? current : { ...current, [id]: height }
		)
	}, [])

	// A lane is as tall as its tallest bar, and each lane starts where the
	// previous one ended. Unmeasured bars fall back to a single lane's height.
	const laneTops: number[] = []
	let cursor = CATEGORY_PADDING_Y

	for (const lane of lanes) {
		const laneHeight = lane.reduce(
			(tallest, positioned) =>
				Math.max(
					tallest,
					barHeights[positioned.activity.title] ?? LANE_HEIGHT
				),
			LANE_HEIGHT
		)
		laneTops.push(cursor)
		cursor += laneHeight + LANE_GAP
	}

	const rowHeight = lanes.length
		? cursor - LANE_GAP + CATEGORY_PADDING_Y
		: LANE_HEIGHT + CATEGORY_PADDING_Y * 2

	return (
		<div
			className='flex border-neutral-200 border-b'
			style={{ height: rowHeight }}
		>
			<div
				className='sticky left-0 flex shrink-0 items-center border-neutral-200 border-r bg-neutral-white px-3'
				style={{
					width: CATEGORY_COLUMN_WIDTH,
					zIndex: Z_CATEGORY_COLUMN
				}}
			>
				<span className='font-semibold text-neutral-900 text-sm'>
					{title}
				</span>
			</div>

			<div className='relative shrink-0' style={{ width: timelineWidth }}>
				{lanes.length ? (
					lanes.map((lane, laneIndex) =>
						lane.map((positioned) => (
							<GanttActivityBar
								dayWidth={dayWidth}
								key={positioned.activity.title}
								onHeightChange={handleHeightChange}
								onSelect={onActivitySelect}
								positioned={positioned}
								top={laneTops[laneIndex] ?? CATEGORY_PADDING_Y}
							/>
						))
					)
				) : (
					<span
						className='pointer-events-none sticky left-3 text-neutral-500 text-xs italic'
						style={{ top: CATEGORY_PADDING_Y }}
					>
						{t('roadmaps:empty_category')}
					</span>
				)}
			</div>
		</div>
	)
}

export { GanttCategoryRow }
